import React, { useEffect, useRef, useState } from 'react';
import {
  ChartViewport,
  clamp,
  createChartViewport,
  getChartViewportSize,
  MIN_VIEWPORT_POINTS,
} from './chartPerformance';

export type ChartViewportAxis = 'x' | 'y';

const ZOOM_IN_WINDOW_FACTOR = 0.8;
const ZOOM_OUT_WINDOW_FACTOR = 1.25;
const CENTER_ANCHOR_RATIO = 0.5;

export interface ChartViewportContainerProps {
  onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: () => void;
  onMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseUp: () => void;
  onTouchCancel: () => void;
  onTouchEnd: (event: React.TouchEvent<HTMLDivElement>) => void;
  onTouchMove: (event: React.TouchEvent<HTMLDivElement>) => void;
  onTouchStart: (event: React.TouchEvent<HTMLDivElement>) => void;
  onWheel: (event: React.WheelEvent<HTMLDivElement>) => void;
}

export interface ChartViewportControls {
  canZoomIn: boolean;
  canZoomOut: boolean;
  containerClassName: string;
  containerProps: ChartViewportContainerProps;
  containerStyle: React.CSSProperties;
  isZoomed: boolean;
  resetZoom: () => void;
  viewport: ChartViewport;
  zoomIn: () => void;
  zoomOut: () => void;
}

interface UseChartViewportControlsOptions {
  axis?: ChartViewportAxis;
  dataLength: number;
  enabled: boolean;
}

/**
 * Shared viewport (zoom + pan) interactions for chart widgets. Supports every
 * input method the dashboard targets: Shift + wheel zoom and drag panning for
 * mouse/trackpad, pinch zoom and one-finger panning for touch screens, and
 * explicit zoom in/out/reset buttons (rendered in the widget header, wired up
 * through `WidgetZoomControlsContext`) that work everywhere. Touch gestures
 * rely on the `touch-action` container style rather
 * than `preventDefault()` because React attaches touch listeners passively.
 */
export const useChartViewportControls = ({
  axis = 'x',
  dataLength,
  enabled,
}: UseChartViewportControlsOptions): ChartViewportControls => {
  const [viewport, setViewport] = useState<ChartViewport>({
    startIndex: 0,
    endIndex: Math.max(dataLength - 1, 0),
  });
  const dragStartPositionRef = useRef<number | null>(null);
  const dragStartViewportRef = useRef<ChartViewport | null>(null);
  const pinchStartDistanceRef = useRef<number | null>(null);
  const pinchStartViewportRef = useRef<ChartViewport | null>(null);
  const pinchAnchorRatioRef = useRef<number>(CENTER_ANCHOR_RATIO);
  const frameRef = useRef<number | null>(null);
  const pendingViewportRef = useRef<ChartViewport | null>(null);

  useEffect(() => {
    setViewport((currentViewport: ChartViewport): ChartViewport => {
      return createChartViewport(
        currentViewport.startIndex,
        getChartViewportSize(currentViewport),
        dataLength
      );
    });
  }, [dataLength]);

  useEffect(() => {
    return (): void => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  const windowSize = getChartViewportSize(viewport);
  const minWindowSize = Math.min(MIN_VIEWPORT_POINTS, Math.max(dataLength, 1));

  const scheduleViewportUpdate = (nextViewport: ChartViewport): void => {
    pendingViewportRef.current = nextViewport;

    if (frameRef.current !== null) {
      return;
    }

    frameRef.current = window.requestAnimationFrame((): void => {
      frameRef.current = null;

      if (pendingViewportRef.current) {
        setViewport(pendingViewportRef.current);
        pendingViewportRef.current = null;
      }
    });
  };

  // For non-scheduled updates (wheel, buttons, reset): a pan/pinch frame may
  // still be queued, and letting it fire afterwards would overwrite this
  // update — so cancel it first.
  const applyViewportNow = (nextViewport: ChartViewport): void => {
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    pendingViewportRef.current = null;
    setViewport(nextViewport);
  };

  const getAxisPosition = (clientX: number, clientY: number, rect: DOMRect): number => {
    return axis === 'y' ? clientY - rect.top : clientX - rect.left;
  };

  const getAxisSize = (rect: DOMRect): number => {
    return axis === 'y' ? rect.height : rect.width;
  };

  const getAnchorRatio = (clientX: number, clientY: number, rect: DOMRect): number => {
    const size = getAxisSize(rect);

    if (size <= 0) {
      return CENTER_ANCHOR_RATIO;
    }

    return clamp(getAxisPosition(clientX, clientY, rect) / size, 0, 1);
  };

  const createZoomedViewport = (
    nextWindowSize: number,
    anchorRatio: number,
    baseViewport: ChartViewport
  ): ChartViewport => {
    const currentWindowSize = getChartViewportSize(baseViewport);
    const safeWindowSize = clamp(nextWindowSize, minWindowSize, Math.max(dataLength, 1));
    const anchorIndex = baseViewport.startIndex + Math.round((currentWindowSize - 1) * anchorRatio);
    const nextStartIndex = Math.round(anchorIndex - (safeWindowSize - 1) * anchorRatio);

    return createChartViewport(nextStartIndex, safeWindowSize, dataLength);
  };

  const getLatestViewport = (): ChartViewport => {
    return pendingViewportRef.current ?? viewport;
  };

  const startDrag = (position: number): void => {
    dragStartPositionRef.current = position;
    dragStartViewportRef.current = getLatestViewport();
  };

  const stopDrag = (): void => {
    dragStartPositionRef.current = null;
    dragStartViewportRef.current = null;
  };

  const stopPinch = (): void => {
    pinchStartDistanceRef.current = null;
    pinchStartViewportRef.current = null;
    pinchAnchorRatioRef.current = CENTER_ANCHOR_RATIO;
  };

  const updateDragViewport = (position: number, rect: DOMRect): void => {
    if (dragStartPositionRef.current === null || !dragStartViewportRef.current) {
      return;
    }

    const axisSize = getAxisSize(rect);

    if (axisSize <= 0) {
      return;
    }

    const dragDistance = position - dragStartPositionRef.current;
    const startViewport = dragStartViewportRef.current;
    const startWindowSize = getChartViewportSize(startViewport);
    const dataPointsPerPixel = startWindowSize / axisSize;
    const indexDelta = Math.round(dragDistance * dataPointsPerPixel);

    scheduleViewportUpdate(
      createChartViewport(startViewport.startIndex - indexDelta, startWindowSize, dataLength)
    );
  };

  const getTouchDistance = (touches: React.TouchList): number => {
    return Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY
    );
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>): void => {
    if (!enabled || !event.shiftKey) {
      return;
    }

    event.preventDefault();

    const rect = event.currentTarget.getBoundingClientRect();
    const anchorRatio = getAnchorRatio(event.clientX, event.clientY, rect);
    const zoomFactor = event.deltaY < 0 ? ZOOM_IN_WINDOW_FACTOR : ZOOM_OUT_WINDOW_FACTOR;
    const baseViewport = getLatestViewport();

    applyViewportNow(
      createZoomedViewport(
        Math.round(getChartViewportSize(baseViewport) * zoomFactor),
        anchorRatio,
        baseViewport
      )
    );
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!enabled || event.button !== 0) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    startDrag(getAxisPosition(event.clientX, event.clientY, rect));
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!enabled) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    updateDragViewport(getAxisPosition(event.clientX, event.clientY, rect), rect);
  };

  const handleMouseEnd = (): void => {
    stopDrag();
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (!enabled) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();

    if (event.touches.length >= 2) {
      stopDrag();
      pinchStartDistanceRef.current = getTouchDistance(event.touches);
      pinchStartViewportRef.current = getLatestViewport();
      pinchAnchorRatioRef.current = getAnchorRatio(
        (event.touches[0].clientX + event.touches[1].clientX) / 2,
        (event.touches[0].clientY + event.touches[1].clientY) / 2,
        rect
      );
      return;
    }

    if (event.touches.length === 1) {
      startDrag(getAxisPosition(event.touches[0].clientX, event.touches[0].clientY, rect));
    }
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (!enabled) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();

    if (
      event.touches.length >= 2 &&
      pinchStartDistanceRef.current !== null &&
      pinchStartViewportRef.current
    ) {
      const distance = getTouchDistance(event.touches);

      if (distance <= 0 || pinchStartDistanceRef.current <= 0) {
        return;
      }

      const startWindowSize = getChartViewportSize(pinchStartViewportRef.current);
      const nextWindowSize = Math.round(
        startWindowSize * (pinchStartDistanceRef.current / distance)
      );

      scheduleViewportUpdate(
        createZoomedViewport(
          nextWindowSize,
          pinchAnchorRatioRef.current,
          pinchStartViewportRef.current
        )
      );
      return;
    }

    if (event.touches.length === 1) {
      updateDragViewport(
        getAxisPosition(event.touches[0].clientX, event.touches[0].clientY, rect),
        rect
      );
    }
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (event.touches.length < 2) {
      stopPinch();
    }

    if (event.touches.length === 1) {
      // Pinch released down to one finger: continue as a pan from the
      // remaining touch instead of jumping.
      const rect = event.currentTarget.getBoundingClientRect();
      startDrag(getAxisPosition(event.touches[0].clientX, event.touches[0].clientY, rect));
      return;
    }

    if (event.touches.length === 0) {
      stopDrag();
    }
  };

  const handleTouchCancel = (): void => {
    stopDrag();
    stopPinch();
  };

  const zoomByFactor = (zoomFactor: number): void => {
    const baseViewport = getLatestViewport();

    applyViewportNow(
      createZoomedViewport(
        Math.round(getChartViewportSize(baseViewport) * zoomFactor),
        CENTER_ANCHOR_RATIO,
        baseViewport
      )
    );
  };

  const zoomIn = (): void => {
    zoomByFactor(ZOOM_IN_WINDOW_FACTOR);
  };

  const zoomOut = (): void => {
    zoomByFactor(ZOOM_OUT_WINDOW_FACTOR);
  };

  const resetZoom = (): void => {
    stopDrag();
    stopPinch();
    applyViewportNow(createChartViewport(0, dataLength, dataLength));
  };

  return {
    canZoomIn: enabled && windowSize > minWindowSize,
    canZoomOut: enabled && windowSize < dataLength,
    containerClassName: enabled ? 'cursor-grab select-none active:cursor-grabbing' : '',
    containerProps: {
      onMouseDown: handleMouseDown,
      onMouseLeave: handleMouseEnd,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseEnd,
      onTouchCancel: handleTouchCancel,
      onTouchEnd: handleTouchEnd,
      onTouchMove: handleTouchMove,
      onTouchStart: handleTouchStart,
      onWheel: handleWheel,
    },
    // Let the browser keep scrolling the page along the chart's cross axis;
    // gestures along the pan axis (and pinches) reach the handlers above.
    containerStyle: enabled ? { touchAction: axis === 'y' ? 'pan-x' : 'pan-y' } : {},
    isZoomed: windowSize < dataLength,
    resetZoom,
    viewport,
    zoomIn,
    zoomOut,
  };
};
